/** SUNTRONIX ONE — MY ACTION CENTRE (/today). "What needs my attention today?" */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Search, Users, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { ActionRow } from "./ActionRow";
import { ActionDialog } from "./ActionDialogs";
import { AiLauncher } from "@/components/askai/AiLauncher";
import AlertsStrip from "@/components/alerts/AlertsStrip";
import { QuickActions } from "@/pages/home/QuickActions";
import { EntityLetterheadPanel } from "@/components/brand/EntityLetterheadPanel";

const COUNTERS = [
  { key: "pending_today", label: "Pending Today", filter: "today", tone: "text-slate-900" },
  { key: "overdue", label: "Overdue", filter: "overdue", tone: "text-rose-700" },
  { key: "critical", label: "Critical", filter: "critical", tone: "text-rose-700" },
  { key: "awaiting_customer", label: "Awaiting Customer", filter: "customer", tone: "text-sky-700" },
  { key: "awaiting_vendor", label: "Awaiting Vendor", filter: "vendor", tone: "text-amber-700" },
  { key: "awaiting_internal", label: "Awaiting Internal Team", filter: "internal", tone: "text-slate-700" },
  { key: "completed_today", label: "Completed Today", filter: "completed", tone: "text-emerald-700" },
];

function ScopeNote({ es }) {
  if (!es || es.mode !== "COMPANY" || es.id === "est_suntronix") return null;
  return <div className="mt-0.5 inline-block rounded bg-sky-50 px-2 py-0.5 text-[10.5px] font-semibold text-sky-900" title="Quotes, approvals, invoices / collections and delivery follow-ups are this company's; items marked GROUP are shared across ONE GROUP" data-testid="ac-scope-note">{es.name} overview · items marked GROUP are shared</div>;
}

export default function ActionCentre() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(sp.get("scope") === "team" ? "team" : "me");
  const [q, setQ] = useState(sp.get("q") || "");
  const [collapsed, setCollapsed] = useState({});
  const [dialog, setDialog] = useState(null); // {kind, item}
  const [landing, setLanding] = useState(null);
  const filter = sp.get("filter") || "all";

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/action-centre/board", { params: { scope, q, filter } });
      setBoard(data);
    } catch { toast.error("Could not load My Action Centre"); }
    finally { setLoading(false); }
  }, [scope, q, filter]);

  useEffect(() => { const t = setTimeout(() => load(), q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);
  useLiveRefresh(async (signal) => {
    const { data } = await api.get("/action-centre/board", { signal, params: { scope, q, filter } });
    if (!signal.aborted) setBoard(data);
  }, { refreshKey: JSON.stringify([scope, q, filter]), enabled: !!board, intervalMs: 60000 });
  useEffect(() => { api.get("/one-today/landing").then(({ data }) => setLanding(data.landing)).catch(() => {}); }, []);

  const setFilter = (f) => { const n = new URLSearchParams(sp); if (f === "all") n.delete("filter"); else n.set("filter", f); setSp(n); };

  const run = async (action) => {
    if (action.kind === "route") { navigate(action.route); return; }
    try {
      await api.request({ method: action.api.method, url: action.api.url, data: action.api.body });
      toast.success("Done"); load(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  const post = async (url, body, msg) => {
    try { await api.post(url, body); if (msg) toast.success(msg); load(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  const handlers = {
    onOpen: (it) => it.route && navigate(it.route),
    onAction: run,
    onComplete: (it) => post("/one-today/complete", { key: it.key }, "Marked complete"),
    onReopen: (it) => post("/action-centre/reopen", { key: it.key }, "Reopened"),
    onSnooze: (it, until) => post("/one-today/snooze", { key: it.key, until }, "Snoozed"),
    onDialog: (kind, it) => setDialog({ kind, item: it }),
  };

  const setLandingPref = async (checked) => {
    const val = checked ? "today" : "enterprise";
    setLanding(val); window.__oneTodayLanding = val;
    await api.post("/one-today/landing", { landing: val });
    toast.success(checked ? "My Action Centre is your landing page" : "Enterprise Home is your landing page");
  };

  const firstName = (user?.name || user?.email || "").split(/[ @]/)[0];
  const groups = useMemo(() => board?.groups || [], [board]);

  return (
    <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 space-y-5" data-testid="action-centre-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] font-semibold tracking-[0.18em] uppercase text-sky-600">My Action Centre · {board?.date || ""}</div>
          <ScopeNote es={board?.establishment_scope} />
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="ac-heading">
            What needs my attention today, {firstName}?
          </h1>
          <p className="text-[12px] text-slate-500 mt-0.5">Request → Assign → Action → Track → Close → Learn</p>
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          {board?.is_management && (
            <div className="flex rounded-md border border-slate-200 overflow-hidden text-[12px]">
              <button data-testid="today-scope-me" onClick={() => setScope("me")}
                className={`px-3 py-1.5 ${scope === "me" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>My work</button>
              <button data-testid="today-scope-team" onClick={() => setScope("team")}
                className={`px-3 py-1.5 ${scope === "team" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                <Users className="h-3.5 w-3.5 inline mr-1" />Team</button>
            </div>
          )}
          <label className="flex items-center gap-2 text-[12px] text-slate-600" data-testid="today-landing-toggle">
            <Switch checked={landing === "today"} onCheckedChange={setLandingPref} /> Land here at login
          </label>
        </div>
      </div>

      <QuickActions />
      <EntityLetterheadPanel />
      <AiLauncher />
      <AlertsStrip businessOnly />

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="ac-counters">
        {COUNTERS.map((c) => {
          const active = filter === c.filter;
          return (
            <button key={c.key} data-testid={`ac-counter-${c.key}`} onClick={() => setFilter(active ? "all" : c.filter)}
              className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${active ? "text-slate-300" : "text-slate-500"}`}>{c.label}</div>
              <div className={`font-heading text-2xl font-bold mt-0.5 ${active ? "text-white" : c.tone}`}>{board?.counters?.[c.key] ?? "—"}</div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, record, status, next action…"
            className="pl-9 h-9" data-testid="today-search-input" />
        </div>
        {filter !== "all" && (
          <button onClick={() => setFilter("all")} data-testid="ac-clear-filter"
            className="text-[12px] text-sky-700 hover:underline">Clear filter · showing {board?.total ?? 0}</button>
        )}
        <span className="ml-auto text-[12px] text-slate-400">{board?.total ?? 0} items{board?.snoozed_count ? ` · ${board.snoozed_count} snoozed` : ""}</span>
      </div>

      {loading && !board ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-16 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Building your day…</div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-emerald-200 bg-emerald-50/40 px-4 py-8 text-sm text-emerald-800 flex items-center gap-2 justify-center" data-testid="ac-empty">
          <CheckCircle2 className="h-4 w-4" /> Nothing needs your attention{filter !== "all" ? " in this view" : " right now"}.
        </div>
      ) : groups.map((g) => {
        const open = !collapsed[g.category];
        return (
          <section key={g.category} data-testid={`ac-group-${g.category.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <button onClick={() => setCollapsed((c) => ({ ...c, [g.category]: open }))}
              className="flex items-center gap-2 mb-2 group">
              {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
              <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">{g.category}</h2>
              <span className="text-[11px] font-mono text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">{g.count}</span>
            </button>
            {open && (
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <div className="hidden lg:grid grid-cols-[1.2fr_2fr_1fr_0.7fr_0.9fr_0.8fr_0.6fr_1.4fr] gap-2 px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  <span>Customer</span><span>Task / transaction</span><span>Owner</span><span>Priority</span><span>Status</span><span>Due</span><span>Ageing</span><span>Next action</span>
                </div>
                {g.items.map((it) => <ActionRow key={it.key} item={it} completedView={filter === "completed"} {...handlers} />)}
              </div>
            )}
          </section>
        );
      })}

      <ActionDialog dialog={dialog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(true); }} />
    </div>
  );
}
